import { useCallback, useRef } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { mutate } from 'swr';
import {
  AlertCode,
  ProcessCheckoutPayload,
  ProcessCheckoutResponse,
  PSP,
  StripePaymentResponse,
  UNKNOWN_PSP,
  ValidateCheckoutResponse,
} from 'shared/types/checkout';
import { WithSDKError } from 'shared/types/shared';
import { getEnv, getEnvOrFail } from 'env/env';
import { sdk } from 'sdk';
import {
  clearAlert,
  getAccountId,
  setAlert,
  setPaymentCTAState,
  useAppDispatch,
  useAppSelector,
} from 'store';
import {
  getAlertCodeForError,
  getAlertCodeFromError,
  makeSDKCallWithRetries,
} from 'utils';
import { withCookieValidation } from 'utils/edx-auth';
import { logout } from 'frontastic/utils/logout';
import { buildThankYouProcessingPath, THANK_YOU_PAGE_PATH } from './constants';
import {
  buildPaymentReturnUrl,
  getFinalizeStripeCheckoutPayload,
  getLineItemsWithBrandText,
  getProcessCheckoutPayloadForPSP,
  getReleaseStripeCheckoutAfterFailurePayload,
  getValidateCheckoutPayloadForPSP,
  sendCohesionConversionEvent,
  sendOrderCompletedEvent,
  sendPaymentInfoEnteredEvent,
} from './helpers';
import { requiresClientConfirmPayment } from './requiresClientConfirmPayment';
import {
  clearCheckoutReturnSession,
  storeCheckoutResumeToken,
  storeStripePublishableKey,
} from './resumeTokenStorage';
import {
  HandleCheckoutProcessOptions,
  PlaceFreeOrderOptions,
  PlaceOrderOptions,
} from './types';

type ConversionContextResponse = {
  isLocked: boolean;
  rate: number;
  payableCurrency: string;
  lockIdentifier?: string;
  lockExpiresAt?: string;
};

const extractPaymentIntentIdFromClientSecret = (
  clientSecret: string,
): string | null => {
  const paymentIntentId = clientSecret.split('_secret')[0];
  return paymentIntentId?.startsWith('pi_') ? paymentIntentId : null;
};

const maybeReleaseCartAfterFailure = async ({
  paymentIntentId,
  resumeToken,
}: {
  paymentIntentId: string | null;
  resumeToken: string;
}) => {
  if (!paymentIntentId) {
    return;
  }

  try {
    await withCookieValidation(() =>
      sdk.callAction<WithSDKError<{ released: boolean }>>(
        getReleaseStripeCheckoutAfterFailurePayload({
          paymentIntentId,
          resumeToken,
        }),
      ),
    );
  } catch (releaseError) {
    console.error(
      'Failed to release cart after non-card Stripe payment failure',
      releaseError,
    );
  }
};

export const useCheckout = () => {
  const dispatch = useAppDispatch();
  const customerId = useAppSelector(getAccountId);

  const router = useRouter();
  const params = useParams();
  const rawLocale = params?.locale;
  const locale =
    (typeof rawLocale === 'string'
      ? rawLocale
      : Array.isArray(rawLocale)
        ? rawLocale[0]
        : undefined) ?? 'en';
  const isRefreshingRef = useRef(false);

  const handleCheckoutProcess = useCallback(
    async ({
      psp,
      cartID,
      priceDetails,
      pspData,
      selectedCurrency,
      showUpi,
      stripePaymentMethodType,
      stripe,
      stripePublishableKey,
      locale: localeOverride,
    }: HandleCheckoutProcessOptions) => {
      let response;
      const { actionName, payload } = getValidateCheckoutPayloadForPSP({
        psp,
        cartID,
        priceDetails,
        pspData,
        selectedCurrency,
        showUpi,
      });

      const validateResponse = await withCookieValidation(() =>
        makeSDKCallWithRetries(
          actionName,
          customerId,
          () =>
            sdk.callAction<ValidateCheckoutResponse>({
              actionName,
              payload,
            }),
          {
            retryErrorCodes: [
              AlertCode.PRODUCT_ENTITLEMENTS_ENROLLEMENT_FETCH_ERROR,
            ],
          },
        ),
      );

      if (validateResponse.isError || validateResponse.data.isError) {
        response = validateResponse;
      } else if (
        psp === PSP.STRIPE &&
        requiresClientConfirmPayment(stripePaymentMethodType)
      ) {
        const { ctPaymentID, clientSecret, resumeToken } =
          validateResponse.data;

        console.info('Stripe checkout method gate', {
          payment_method_branch: 'non-card',
          payment_method_type: stripePaymentMethodType,
        });

        if (
          typeof clientSecret !== 'string' ||
          clientSecret.length === 0 ||
          typeof resumeToken !== 'string' ||
          resumeToken.length === 0
        ) {
          return {
            isError: true as const,
            error: {
              message:
                'Missing clientSecret or resumeToken for non-card Stripe checkout',
              code: 'missing_client_confirm_credentials',
              status: 400,
            },
          };
        }

        if (!stripe) {
          return {
            isError: true as const,
            error: {
              message: 'Stripe.js is not loaded for client confirmPayment',
              code: 'stripe_not_loaded',
              status: 500,
            },
          };
        }

        // Resolved once up front so the resume token is stored and cleared
        // under the same PaymentIntent-scoped key throughout this attempt —
        // the same id /payment-return will read from its own query params.
        const returnPaymentIntentId =
          extractPaymentIntentIdFromClientSecret(clientSecret);

        if (!returnPaymentIntentId) {
          return {
            isError: true as const,
            error: {
              message: 'Unable to derive a PaymentIntent id from clientSecret',
              code: 'missing_client_confirm_credentials',
              status: 400,
            },
          };
        }

        storeCheckoutResumeToken(returnPaymentIntentId, resumeToken);
        if (stripePublishableKey) {
          storeStripePublishableKey(stripePublishableKey);
        }

        const returnLocale = localeOverride ?? locale;
        const confirmResult = await stripe.confirmPayment({
          clientSecret,
          confirmParams: {
            return_url: buildPaymentReturnUrl(
              window.location.origin,
              returnLocale,
            ),
          },
          redirect: 'if_required',
        });

        if (confirmResult.error) {
          const paymentIntentId =
            confirmResult.error.payment_intent?.id ??
            extractPaymentIntentIdFromClientSecret(clientSecret);

          await maybeReleaseCartAfterFailure({
            paymentIntentId,
            resumeToken,
          });
          clearCheckoutReturnSession(returnPaymentIntentId, resumeToken);

          // Every UPI payment failure shows the shopper the same alert, so
          // this message only feeds operational logs — keep Stripe's own
          // codes in it. A decline code can land directly on the error or
          // nested on the PaymentIntent it carries, depending on when
          // Stripe detects the problem.
          const declineCode =
            confirmResult.error.decline_code ??
            confirmResult.error.payment_intent?.last_payment_error
              ?.decline_code;

          return {
            isError: true as const,
            error: {
              message: `UPI confirmPayment failed (code: ${confirmResult.error.code ?? 'unknown'}, decline_code: ${declineCode ?? 'none'}): ${confirmResult.error.message ?? 'no message'}`,
              code: 'upi_payment_failed',
              status: 400,
            },
          };
        }

        const { paymentIntent } = confirmResult;
        if (paymentIntent?.status === 'processing') {
          // No order yet — thank-you shows pending via payment_status=processing.
          // Keep cart frozen for webhook recovery; do not finalize or unfreeze.
          clearCheckoutReturnSession(returnPaymentIntentId, resumeToken);
          router.push(buildThankYouProcessingPath());
          return {
            isError: false as const,
            paymentPending: true as const,
            order: null,
          };
        }

        if (paymentIntent?.status !== 'succeeded') {
          // Fallback for the less common case where Stripe resolves
          // without throwing an `error` (already handled above) but still
          // leaves a non-succeeded PaymentIntent with decline details on
          // last_payment_error.
          const paymentIntentId =
            paymentIntent?.id ??
            extractPaymentIntentIdFromClientSecret(clientSecret);
          await maybeReleaseCartAfterFailure({
            paymentIntentId,
            resumeToken,
          });
          clearCheckoutReturnSession(returnPaymentIntentId, resumeToken);

          return {
            isError: true as const,
            error: {
              message: `UPI payment did not complete (PaymentIntent status: ${paymentIntent?.status ?? 'unknown'}, decline_code: ${paymentIntent?.last_payment_error?.decline_code ?? 'none'})`,
              code: 'upi_payment_failed',
              status: 400,
            },
          };
        }

        const finalizeResponse = await withCookieValidation(() =>
          sdk.callAction<StripePaymentResponse>(
            getFinalizeStripeCheckoutPayload({
              paymentIntentId: paymentIntent.id,
              resumeToken,
              ctPaymentID,
              cartID,
            }),
          ),
        );

        if (finalizeResponse.isError || finalizeResponse.data.isError) {
          clearCheckoutReturnSession(returnPaymentIntentId, resumeToken);
          response = finalizeResponse;
        } else {
          clearCheckoutReturnSession(returnPaymentIntentId, resumeToken);
          response = {
            isError: false as const,
            data: {
              isError: false as const,
              order: finalizeResponse.data.order,
              paymentIntentId: finalizeResponse.data.paymentIntentId,
            },
          };
        }
      } else {
        if (psp === PSP.STRIPE) {
          console.info('Stripe checkout method gate', {
            payment_method_branch: 'card',
            payment_method_type: stripePaymentMethodType ?? 'card',
          });
        }

        // pspPaymentID will be paypal order id for PayPal PSP and payment intent id for Stripe PSP
        const { ctPaymentID, payerName } = validateResponse.data;
        response = await withCookieValidation(() =>
          sdk.callAction<ProcessCheckoutResponse>(
            getProcessCheckoutPayloadForPSP({
              psp,
              cartID,
              ctPaymentID,
              payerName,
            }),
          ),
        );
      }

      return response.isError
        ? response
        : (response.data as ProcessCheckoutResponse);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId, locale],
  );

  const placeOrder = useCallback(
    async ({
      cartID,
      lineItems,
      priceDetails,
      paymentMethodType,
      psp,
      pspData,
      shouldRetrySdnHit = false,
      selectedCurrency,
      showUpi,
      stripePaymentMethodType,
      stripe,
      stripePublishableKey,
      locale: localeOverride,
    }: PlaceOrderOptions) => {
      dispatch(setPaymentCTAState({ psp, value: true }));
      try {
        sendPaymentInfoEnteredEvent({
          cartID,
          lineItems,
          priceDetails,
          paymentMethodType,
        });

        const apiResponse = await handleCheckoutProcess({
          psp,
          cartID,
          priceDetails,
          pspData,
          selectedCurrency,
          showUpi,
          stripePaymentMethodType,
          stripe,
          stripePublishableKey,
          locale: localeOverride,
        });

        if (
          apiResponse &&
          'paymentPending' in apiResponse &&
          apiResponse.paymentPending
        ) {
          // Already navigated to thank-you pending state.
          dispatch(setPaymentCTAState({ psp, value: false }));
          return { isError: false };
        }

        if (apiResponse.isError) {
          const errorCode = (apiResponse.error?.code as string) ?? '';
          const errorMessage =
            apiResponse.error?.message ??
            JSON.stringify(apiResponse.error) ??
            'unknown';

          // SDN handling
          if (errorCode === 'sdn_matched_error') {
            if (shouldRetrySdnHit) {
              dispatch(setAlert({ code: AlertCode.SDN_HIT }));

              return {
                isError: true,
                error: 'sdn_matched_error',
              };
            }
            console.error('SDN matched for customer:', customerId);
            await logout();
            const commerceCoordinatorBaseUrl =
              getEnvOrFail('NEXT_PUBLIC_EDX_COMMERCE_COORDINATOR_BASE_URL') ??
              '';
            const sdnFailurePath =
              getEnvOrFail('NEXT_PUBLIC_EDX_CC_SDN_FAILURE_PATH') ?? '';
            router.replace(commerceCoordinatorBaseUrl + sdnFailurePath);
            return null;
          }
          const alertCode = getAlertCodeForError(apiResponse.error, psp);

          // Handle quote expiry with automatic refresh
          if (alertCode === AlertCode.QUOTE_EXPIRED) {
            const isCurrencyDisplayEnabled =
              getEnv('NEXT_PUBLIC_ENABLE_DYNAMIC_CURRENCY') === 'true';

            if (isCurrencyDisplayEnabled && selectedCurrency) {
              // Only show banner and refresh if not already refreshing
              if (!isRefreshingRef.current) {
                isRefreshingRef.current = true;

                // Show quote expired banner
                dispatch(setAlert({ code: AlertCode.QUOTE_EXPIRED }));

                // Trigger background refresh of conversion context
                try {
                  const refreshResponse =
                    await sdk.callAction<ConversionContextResponse>({
                      actionName: 'conversion/getConversionContext',
                      payload: {
                        cartId: cartID,
                        selectedCurrency,
                      },
                    });

                  if (refreshResponse.isError) {
                    // Refresh failed - show rates unavailable and fall back to USD
                    const errorDetails = refreshResponse.error;
                    dispatch(setAlert({ code: AlertCode.RATES_UNAVAILABLE }));
                    console.error(
                      `Failed to refresh conversion context for customer: ${customerId}`,
                      errorDetails,
                    );
                  } else if (!refreshResponse.data) {
                    // No data returned - show rates unavailable
                    dispatch(setAlert({ code: AlertCode.RATES_UNAVAILABLE }));
                    console.error(
                      `No conversion context data returned for customer: ${customerId}`,
                    );
                  } else {
                    // Refresh succeeded - clear banner and notify components to refresh
                    dispatch(clearAlert());
                    console.info(
                      `Successfully refreshed conversion context for customer: ${customerId}`,
                    );
                    // Dispatch custom event to notify checkout components to refresh conversion context
                    if (typeof window !== 'undefined') {
                      window.dispatchEvent(
                        new CustomEvent('conversionContextRefreshed', {
                          detail: {
                            cartId: cartID,
                            currency: selectedCurrency,
                          },
                        }),
                      );
                    }
                  }
                } catch (refreshError) {
                  // Refresh failed - show rates unavailable
                  dispatch(setAlert({ code: AlertCode.RATES_UNAVAILABLE }));
                  console.error(
                    `Exception refreshing conversion context for customer: ${customerId}`,
                    refreshError,
                  );
                } finally {
                  isRefreshingRef.current = false;
                }
              } else {
                // Already refreshing, just show the banner
                dispatch(setAlert({ code: AlertCode.QUOTE_EXPIRED }));
              }
            } else {
              // Feature flag disabled or no currency - use standard USD error handling
              // Don't show quote expired alert, use generic payment processing failed
              dispatch(setPaymentCTAState({ psp, value: false }));
              dispatch(setAlert({ code: AlertCode.PAYMENT_PROCESSING_FAILED }));
            }
          } else {
            // Not a quote expiry error - use standard error handling
            // Log errors that don't already have specific logging
            console.error(
              `Error in processCheckout extension for ${psp} for customer: ${customerId} with error code: ${alertCode} and error message: ${errorMessage}`,
            );
            dispatch(setPaymentCTAState({ psp, value: false }));
            dispatch(setAlert({ code: alertCode }));
          }

          await mutate('/action/cart/getCart');
          sendCohesionConversionEvent([], 'Cart', 'Declined', priceDetails);
        } else {
          const { order } = apiResponse;
          if (!order) {
            throw new Error('No order found in apiResponse from the server');
          }

          // Check if it's a program (has bundleId) to get updated brand-text
          const isProgram = order.lineItems.some((item) => item.bundleId);

          let lineItemsForEvents = order.lineItems;
          if (isProgram) {
            // Get line items with updated brand-text for program orders
            lineItemsForEvents = await getLineItemsWithBrandText(
              order.lineItems,
            );
          }

          // Track "Completed" conversion for all orders
          sendCohesionConversionEvent(
            lineItemsForEvents,
            'Enrollment',
            'Completed',
            priceDetails,
            order.discountCodes[order.discountCodes.length - 1]?.code,
            order?.orderNumber,
            selectedCurrency,
            paymentMethodType,
          );

          sendOrderCompletedEvent({
            psp,
            cartID,
            priceDetails,
            paymentMethodType,
            order,
          });

          const thankYouParams = new URLSearchParams({
            order_id: order.orderId,
          }).toString();
          router.push(`${THANK_YOU_PAGE_PATH}/?${thankYouParams}`);
        }
      } catch (error) {
        console.error(
          `Order placement error via ${psp} for customer:${customerId}. Error:${
            JSON.stringify(error) ?? 'unknown'
          }`,
        );
        dispatch(setAlert({ code: AlertCode.PAYMENT_PROCESSING_FAILED }));
        dispatch(setPaymentCTAState({ psp, value: false }));
        sendCohesionConversionEvent([], 'Cart', 'Declined', priceDetails);
      }
      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId, handleCheckoutProcess],
  );

  const placeFreeOrder = useCallback(
    async ({ cartID, lineItems, priceDetails }: PlaceFreeOrderOptions) => {
      dispatch(setPaymentCTAState({ psp: UNKNOWN_PSP.Type, value: true }));
      try {
        sendPaymentInfoEnteredEvent({
          cartID,
          lineItems,
          priceDetails,
          paymentMethodType: 'free',
        });

        const payload = {
          cartID,
        } as ProcessCheckoutPayload;

        const apiResponse = await withCookieValidation(() =>
          sdk.callAction<ProcessCheckoutResponse>({
            actionName: 'order/placeFreeOrder',
            payload,
          }),
        );

        if (apiResponse.isError) {
          const errorCode = (apiResponse.error?.code as string) ?? '';
          const errorMessage =
            apiResponse.error?.message ??
            JSON.stringify(apiResponse.error) ??
            'unknown';
          const alertCode = getAlertCodeFromError(apiResponse.error);

          console.error(
            `Error in processCheckout extension while processing for free order
           for customer: ${customerId} with error code: ${errorCode} and error message: ${errorMessage}`,
          );
          dispatch(setAlert({ code: alertCode }));
          dispatch(setPaymentCTAState({ psp: UNKNOWN_PSP.Type, value: false }));

          await mutate('/action/cart/getCart');
          sendCohesionConversionEvent([], 'Cart', 'Declined', priceDetails);
        } else if (apiResponse.data.isError) {
          const { error } = apiResponse.data;
          const alertCode = getAlertCodeFromError(apiResponse.data.error);
          console.error(
            `Error in processCheckout extension for free order for customer: ${customerId} with error message: ${error?.message ?? (error as unknown as string)}`,
          );
          dispatch(
            setAlert({ code: alertCode ?? AlertCode.ORDER_PROCESSING_FAILED }),
          );
          dispatch(setPaymentCTAState({ psp: UNKNOWN_PSP.Type, value: false }));
          sendCohesionConversionEvent([], 'Cart', 'Declined', priceDetails);
        } else {
          const { order } = apiResponse.data;
          if (!order) {
            throw new Error('No order found in apiResponse from the server');
          }

          // Check if it's a program (has bundleId) to get updated brand-text
          const isProgram = order.lineItems.some((item) => item.bundleId);

          let lineItemsForEvents = order.lineItems;
          if (isProgram) {
            // Get line items with updated brand-text for program orders
            lineItemsForEvents = await getLineItemsWithBrandText(
              order.lineItems,
            );
          }

          // Track "Completed" conversion for all orders
          sendCohesionConversionEvent(
            lineItemsForEvents,
            'Enrollment',
            'Completed',
            priceDetails,
            order.discountCodes[order.discountCodes.length - 1]?.code,
            order?.orderNumber,
            priceDetails?.currencyCode,
            UNKNOWN_PSP.Type,
          );

          sendOrderCompletedEvent({
            psp: UNKNOWN_PSP.Type,
            cartID,
            priceDetails,
            paymentMethodType: UNKNOWN_PSP.Type,
            order,
          });

          const thankYouParams = new URLSearchParams({
            order_id: order.orderId,
          }).toString();
          router.push(`${THANK_YOU_PAGE_PATH}/?${thankYouParams}`);
        }
      } catch (error) {
        console.error(
          `Order placement error for free for customer:${customerId}. Error:${
            JSON.stringify(error) ?? 'unknown'
          }`,
        );
        dispatch(setPaymentCTAState({ psp: UNKNOWN_PSP.Type, value: false }));
        dispatch(setAlert({ code: AlertCode.ORDER_PROCESSING_FAILED }));
        sendCohesionConversionEvent([], 'Cart', 'Declined', priceDetails);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId],
  );

  return { placeOrder, placeFreeOrder };
};
